import { useTranslations } from 'next-intl';
import { toast } from 'react-toastify';
import { useCheckoutCartState } from '@monofront/business/contexts';
import { trackEvent } from '@monofront/tracking';
import { PaymentMethodTypes } from '@monofront/types';

import { useAuthentication } from '@context/businessContainer';
import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';
import logger from '@utils/logger';
import { useCheckoutPayment } from '../contexts';
import { isMobileApp } from '@utils/environment';

interface IPaymentErrors {
  code?: string;
  message?: string;
  error?: any;
  context?: Record<string, unknown>;
  shouldDisplayError?: boolean;
}

interface IUseHandlePaymentErrorReturn {
  handlePaymentError: (data: IPaymentErrors) => void;
}

const paymentMethodsTrackingValues = {
  [PaymentMethodTypes.CARD]: 'credit_card',
  [PaymentMethodTypes.PAYPAL]: 'paypal',
  [PaymentMethodTypes.PAYPAL4X]: 'paypal-4x',
  [PaymentMethodTypes.APPLE_PAY]: 'apple_pay',
};

const ERROR_KEYS: Record<
  string,
  Extract<keyof IntlMessages['Checkout']['Payment']['Errors'], string>
> = {
  COMPLETE_ORDER_FAILED: 'cannotCompleteOrder',
  ORDER_OUT_OF_STOCK: 'cannotCompleteOrderOutOfStock',
  PAY_WITH_EXISTING_CARD_FAILED: 'cannotPayWithExistingCard',
  PAY_WITH_EXISTING_PAYPAL_FAILED: 'cannotPayWithExistingPaypal',
  PAY_WITH_NEW_CARD_FAILED: 'cannotPayWithNewCard',
  PAY_WITH_NEW_PAYPAL_FAILED: 'cannotPayWithNewPaypal',
  PAY_WITH_NEW_PAYPAL_AGREEMENT_FAILED: 'cannotPayWithNewPaypal',
  AUTHORIZE_NEW_PAYPAL_BILLING_AGREEMENT_FAILED: 'cannotPayWithNewPaypal',
  PAY_WITH_NEW_APPLEPAY_FAILED: 'cannotPayWithNewApplePay',
  CREATE_NEW_APPLE_PAY_REQUEST_FAILED: 'cannotCreateNewApplePayRequest',
  UNKNOWN: 'generic',
};

const useHandlePaymentError = (): IUseHandlePaymentErrorReturn => {
  const { customer } = useAuthentication();
  const tCheckoutPaymentErrors = useTranslations('Checkout.Payment.Errors');
  const { checkoutCart } = useCheckoutCartState();
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);
  const { selectedPaymentType, selectedPaymentRadioId } = useCheckoutPayment();

  const handlePaymentError = ({
    code,
    message,
    context = {},
    error,
    shouldDisplayError = false,
  }: IPaymentErrors) => {
    const logMessage = `[checkout] ${
      message || 'An error occured on the payment page.'
    }`;
    const errorCode = code || error?.code || 'UNKNOWN';

    const defaultContext = {
      error,
      error_code: errorCode,
      user_id: customer?.uid,
      cart_id: checkoutCart?.id,
      cart_payment_id: checkoutCart?.payment?.id,
      selected_payment: selectedPaymentRadioId,
      with_subscription: isSubscriptionAvailable,
      is_mobile_app: isMobileApp,
      ...context,
    };

    logger.error(logMessage, defaultContext);

    trackEvent({
      type: 'LFCheckoutPaymentError',
      data: {
        error: {
          errorKey: errorCode,
          errorMessage: logMessage,
        },
        paymentMethod: selectedPaymentType
          ? paymentMethodsTrackingValues[selectedPaymentType]
          : 'unknown',
      },
    });

    if (code === 'MAAP_SUBS_FAILED') {
      trackEvent({
        type: 'LFMaapPaymentError',
        data: {
          failureType: 'subscription',
        },
      });
    }

    if (shouldDisplayError) {
      toast(
        tCheckoutPaymentErrors(ERROR_KEYS[errorCode] || ERROR_KEYS['UNKNOWN']),
        {
          className: 'toast-error-pause',
          autoClose: false,
        },
      );
    }
  };

  return { handlePaymentError };
};

export default useHandlePaymentError;
